import React, { useEffect, useMemo } from 'react';
import { View, Text, StyleSheet, ScrollView, Linking } from 'react-native';
import { router } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { CircleDollarSign, ExternalLink, FileText } from 'lucide-react-native';
import {
  useTheme,
  SPACING,
  BORDER_RADIUS,
  TYPOGRAPHY,
  DESKTOP_CONTENT_MAX_WIDTH,
  CONTENT_MAX_WIDTH,
} from '../../src/theme';
import { Header } from '@components/layout';
import { Card, Button, DotLabel, EmptyState, ErrorState, LoadingSpinner } from '@components/common';
import { useDesktopShell } from '@hooks/useDesktopShell';
import { useOwnPlansSubscription } from '@hooks/useOwnPlansSubscription';
import { useFundingStore } from '../../src/store/fundingStore';
import { useServiceAreasStore } from '../../src/store/serviceAreasStore';
import { useAuthStore } from '../../src/store/authStore';
import { useDefensePlansStore } from '../../src/store/defensePlansStore';
import { isPdfDownloadSupported } from '../../src/utils/fundingRequestPdf';
import { KIND_LABEL, STATUS_META } from '../../src/utils/fundingLabels';
import { FundingSource } from '../../src/types';

function formatDate(timestamp: { toDate: () => Date }): string {
  return timestamp.toDate().toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// Counts what's actually open from the loaded data — never a hardcoded
// figure. See the client ask: don't advertise a total behind a closed grant.
function computeHeadline(sources: FundingSource[]): string {
  if (sources.length === 0) return '';
  const openCount = sources.filter((s) => STATUS_META[s.status].open).length;
  const otherCount = sources.length - openCount;
  const openLabel = `${openCount} program${openCount === 1 ? '' : 's'} open now`;
  if (otherCount === 0) return openLabel;
  if (openCount === 0) {
    return `No programs open right now, ${otherCount} more expected`;
  }
  return `${openLabel}, ${otherCount} more expected`;
}

// Open programs first, since they're the ones a person can apply to today.
// The query has no orderBy, so within each group the order stays as
// Firestore returns it (sort is stable).
function openFirst(sources: FundingSource[]): FundingSource[] {
  return [...sources].sort(
    (a, b) => Number(STATUS_META[b.status].open) - Number(STATUS_META[a.status].open)
  );
}

export default function FundingScreen() {
  const { colors } = useTheme();
  const insets = useSafeAreaInsets();
  const isDesktopShell = useDesktopShell();
  const sources = useFundingStore((s) => s.sources);
  const loading = useFundingStore((s) => s.loading);
  const error = useFundingStore((s) => s.error);
  const load = useFundingStore((s) => s.load);
  const areas = useServiceAreasStore((s) => s.areas);
  const areaIds = areas.map((a) => a.id);
  const areaKey = areaIds.join(',');
  const user = useAuthStore((s) => s.user);
  const ownPlans = useDefensePlansStore((s) => s.ownPlans);
  useOwnPlansSubscription();
  // The request page lists only plans the signed-in user owns that have at
  // least one defense.
  const canBuildRequest =
    isPdfDownloadSupported &&
    !!user &&
    ownPlans.some((plan) => plan.ownerId === user.id && plan.features.length > 0);

  useEffect(() => {
    void load(areaIds);
    // areaKey stands in for areaIds: the array itself is a fresh reference
    // every render, and load() is already a no-op when the effective set
    // hasn't changed (see fundingStore's loadedKey).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areaKey, load]);

  const headline = computeHeadline(sources);
  const ordered = useMemo(() => openFirst(sources), [sources]);

  return (
    <View
      style={[styles.container, { backgroundColor: colors.background }]}
      testID="funding-screen">
      <Header large wide title="Funding" />
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={[
          styles.content,
          { maxWidth: isDesktopShell ? DESKTOP_CONTENT_MAX_WIDTH : CONTENT_MAX_WIDTH },
          { paddingBottom: insets.bottom + 100 },
        ]}
        showsVerticalScrollIndicator={false}>
        {canBuildRequest && (
          <View
            style={[styles.requestBlock, { borderColor: colors.border }]}
            testID="funding-request-entry">
            <Text style={[styles.requestBody, { color: colors.textSecondary }]}>
              Pick steps from your defense plan and the sources you might apply to, and download a
              PDF to use in a request for proposals or a contract for services.
            </Text>
            <Button
              title="Build a funding request"
              variant="outline"
              size="sm"
              leftIcon={FileText}
              onPress={() => router.push('/funding/request')}
              testID="funding-request-entry-button"
            />
          </View>
        )}
        {loading && sources.length === 0 ? (
          <LoadingSpinner />
        ) : error ? (
          <ErrorState message={error} onRetry={() => load(areaIds)} />
        ) : sources.length === 0 ? (
          <View testID="funding-empty">
            <EmptyState
              icon={CircleDollarSign}
              title="No funding listed for your area yet"
              message="Check back soon."
            />
          </View>
        ) : (
          <>
            <View style={styles.headlineBlock}>
              <Text style={[styles.headline, { color: colors.text }]} testID="funding-headline">
                {headline}
              </Text>
              <Text style={[styles.caveat, { color: colors.textSecondary }]}>
                Each number below is a program&apos;s maximum award, not money this community has
                already received.
              </Text>
            </View>

            <View style={styles.cards}>
              {ordered.map((source) => (
                <FundingCard key={source.id} source={source} />
              ))}
            </View>

            <View style={[styles.startBlock, { borderTopColor: colors.border }]}>
              <Text style={[styles.startTitle, { color: colors.text }]}>Where to start</Text>
              <Text style={[styles.startBody, { color: colors.textSecondary }]}>
                Most grants above are written by a local government, fire district, or fire safe
                council, not by an individual homeowner. If your community wants one of these, start
                with your fire safe council or fire department. The insurance discount is the
                exception: ask your own insurer to apply it.
              </Text>
            </View>
          </>
        )}
      </ScrollView>
    </View>
  );
}

function FundingCard({ source }: { source: FundingSource }) {
  const { colors } = useTheme();
  const status = STATUS_META[source.status];
  const awardText =
    source.maxAward != null
      ? `Up to $${source.maxAward.toLocaleString('en-US')} maximum award`
      : null;
  const deadlineText = source.deadline
    ? `Deadline: ${formatDate(source.deadline)}`
    : (source.deadlineNote ?? 'No deadline given');

  return (
    <Card variant="outlined" padding="md" style={styles.card} testID={`funding-card-${source.id}`}>
      <View style={styles.cardHeaderRow}>
        <View style={styles.cardHeaderText}>
          <Text style={[styles.cardName, { color: colors.text }]}>{source.name}</Text>
          <Text style={[styles.cardFunder, { color: colors.textSecondary }]}>{source.funder}</Text>
        </View>
        <DotLabel label={status.label} variant={status.open ? 'progress' : 'muted'} />
      </View>

      <Text style={[styles.cardSummary, { color: colors.text }]}>{source.summary}</Text>

      <View style={styles.cardMeta}>
        <DotLabel label={KIND_LABEL[source.kind]} variant="muted" />
        {awardText && <Text style={[styles.cardDetail, { color: colors.text }]}>{awardText}</Text>}
        {source.awardNote && (
          <Text style={[styles.cardDetailSecondary, { color: colors.textSecondary }]}>
            {source.awardNote}
          </Text>
        )}
        <Text style={[styles.cardDetail, { color: colors.text }]}>
          Who applies: {source.whoApplies}
        </Text>
        <Text style={[styles.cardDetail, { color: colors.text }]}>{deadlineText}</Text>
      </View>

      <View style={styles.cardFooter}>
        <Button
          title="Program details"
          variant="outline"
          size="sm"
          rightIcon={ExternalLink}
          onPress={() => void Linking.openURL(source.url)}
          testID={`funding-card-${source.id}-link`}
        />
        <Text style={[styles.checkedText, { color: colors.textTertiary }]}>
          Checked {formatDate(source.sourceCheckedAt)}
        </Text>
      </View>
    </Card>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scroll: {
    flex: 1,
  },
  content: {
    width: '100%',
    alignSelf: 'center',
    padding: SPACING.md,
    gap: SPACING.lg,
  },
  requestBlock: {
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: BORDER_RADIUS.md,
    padding: SPACING.md,
    gap: SPACING.sm,
    alignItems: 'flex-start',
  },
  requestBody: {
    ...TYPOGRAPHY.bodySmall,
  },
  headlineBlock: {
    gap: SPACING.xs,
  },
  headline: {
    ...TYPOGRAPHY.h3,
  },
  caveat: {
    ...TYPOGRAPHY.bodySmall,
  },
  cards: {
    gap: SPACING.md,
  },
  card: {
    gap: SPACING.sm,
  },
  cardHeaderRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: SPACING.sm,
  },
  cardHeaderText: {
    flex: 1,
  },
  cardName: {
    ...TYPOGRAPHY.button,
  },
  cardFunder: {
    ...TYPOGRAPHY.bodySmall,
    marginTop: 2,
  },
  cardSummary: {
    ...TYPOGRAPHY.body,
  },
  cardMeta: {
    gap: SPACING.xxs,
  },
  cardDetail: {
    ...TYPOGRAPHY.bodySmall,
  },
  cardDetailSecondary: {
    ...TYPOGRAPHY.bodySmall,
  },
  cardFooter: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: SPACING.sm,
    marginTop: SPACING.xs,
  },
  checkedText: {
    ...TYPOGRAPHY.caption,
  },
  startBlock: {
    borderTopWidth: StyleSheet.hairlineWidth,
    paddingTop: SPACING.md,
    gap: SPACING.xs,
  },
  startTitle: {
    ...TYPOGRAPHY.button,
  },
  startBody: {
    ...TYPOGRAPHY.bodySmall,
  },
});
